import { BUILDINGS, INVASION, SHIP_COMBAT, TRADE_AGREEMENT, type AgreementId } from '@sd/content';
import type { FleetState } from './fleet';
import type { PlanetState } from './planet';

/**
 * Diplomacy and war state (GDD §8). Pure helpers over small structural
 * types, so the match (game.ts) can use them without import cycles.
 */
export interface Agreement {
  readonly kind: AgreementId;
  readonly a: string;
  readonly b: string;
}

/** Whether two players are on one team (#137): their alliance is for the whole match. */
export function sameTeam(
  players: readonly { readonly id: string; readonly team?: number }[],
  a: string,
  b: string,
): boolean {
  const ta = players.find((p) => p.id === a)?.team;
  return ta !== undefined && ta === players.find((p) => p.id === b)?.team;
}

/** A player and their teammates (#137); alone without a team. */
export function teamOf(
  players: readonly { readonly id: string; readonly team?: number }[],
  id: string,
): string[] {
  const team = players.find((p) => p.id === id)?.team;
  return team === undefined ? [id] : players.filter((p) => p.team === team).map((p) => p.id);
}

/** The alliances a match starts with: every pair of teammates (#137). */
export function teamAlliances(
  players: readonly { readonly id: string; readonly team?: number }[],
): Agreement[] {
  const out: Agreement[] = [];
  for (let i = 0; i < players.length; i++)
    for (let j = i + 1; j < players.length; j++)
      if (sameTeam(players, players[i]!.id, players[j]!.id))
        out.push({ kind: 'alliance', a: players[i]!.id, b: players[j]!.id });
  return out;
}

export interface Proposal {
  readonly kind: AgreementId | 'peace';
  readonly from: string;
  readonly to: string;
}

export interface PendingWar {
  readonly by: string;
  readonly target: string;
  readonly startsAt: number;
}

/** A fleet holding a target to plunder it (GDD §8: 20 s). */
export interface Raid {
  readonly fleetId: number;
  readonly owner: string;
  readonly victim: string;
  readonly target: { readonly kind: 'planet' } | { readonly kind: 'outpost'; readonly id: string };
  readonly progress: number;
}

/** Troops on the ground, holding out for the capital to fall (GDD §8: 90 s). */
export interface Siege {
  readonly owner: string;
  readonly victim: string;
  readonly troops: number;
  readonly progress: number;
}

/** Deliveries made between two partners under a trade agreement (#74). */
export interface TradeRoute {
  readonly a: string;
  readonly b: string;
  readonly deliveries: number;
}

/** Level of the trade route between two players, 1..3; 0 without a trade agreement in force. */
export function routeLevel(
  agreements: readonly Agreement[],
  routes: readonly TradeRoute[],
  a: string,
  b: string,
): number {
  if (!hasAgreement(agreements, a, b, 'trade')) return 0;
  const deliveries = routes.find((r) => pairIs(r, a, b))?.deliveries ?? 0;
  return 1 + TRADE_AGREEMENT.routeLevels.filter((n) => deliveries >= n).length;
}

export const pairIs = (x: { a: string; b: string }, a: string, b: string): boolean =>
  (x.a === a && x.b === b) || (x.a === b && x.b === a);

export const hasAgreement = (
  agreements: readonly Agreement[],
  a: string,
  b: string,
  kind: AgreementId,
): boolean => agreements.some((x) => x.kind === kind && pairIs(x, a, b));

/** Damage per round of a fleet's armed ships. */
export const fleetDamage = (f: FleetState): number =>
  f.ships.reduce((sum, s) => sum + SHIP_COMBAT[s.cls].damage, 0);

/** Damage per round of a capital's guns. */
export const planetGuns = (planet: PlanetState): number =>
  planet.buildings.reduce((sum, b) => sum + BUILDINGS[b.id].guns * b.level, 0);

/** Troops it takes more than to take the capital: militia + garrisons + shields. */
export function garrisonStrength(planet: PlanetState): number {
  return planet.buildings.reduce<number>(
    (sum, b) =>
      sum +
      BUILDINGS[b.id].garrison * b.level +
      BUILDINGS[b.id].shield * b.level * INVASION.shieldToGarrison,
    INVASION.baseGarrison,
  );
}
